import classNames from "classnames";
import { styled } from "@linaria/react";
import React, { useCallback } from "react";
import { color, easingBounce, easingBounceMs, radius, fonts } from "..";
import useEnsuredForwardedRef from "../lib/hooks/useEnsuredForwardedRef";
import OptionList from "../OptionList";
import Popper from "../Popper";
import useFilteredOptions from "./useFilteredOptions";
import usePrevious from "../lib/hooks/usePrevious";
import t from "../i18n";
import { HIDE_ON_EXPORT } from "../lib/downloadPage";
import type { Props as FieldProps } from "../Form/Field";
import Field from "../Form/Field";
import { css } from "@linaria/core";
import { colors, shadows, spacings } from "../Theme";
import SearchInputV2 from "../Input/SearchInputV2";
import ClearButton from "../Input/ClearButton";
import { ArrowDownSLine } from "../IconV2/react";
import TooltipV2 from "../TooltipV2";
import { FragmentSlot, OptionV2 } from "../Option/v2";
import Checkbox from "../Checkbox";

export type SelectOption<T extends string = string> = {
  label: string;
  description?: string;
  value: T;
  icon?: React.ReactNode;
  disabled?: boolean;
  extra?: React.ReactNode;
};

const IconOption = styled.div`
  color: ${color.titanium600};
`;

interface P extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "as" | "children"> {
  prompt: string;
  forcePromptWhenOptionSelected?: boolean; // make the prompt appear even if an option is selected
  options: SelectOption[];
  children?: never;
  disabled?: boolean;
  error?: React.ReactNode;
  filtered?: boolean;
  buttonRef?: React.Ref<HTMLButtonElement>;
  tooltip?: React.ReactNode;
  clearable?: boolean;
  autoScrollToSelected?: boolean;
  noOptionsLabelElement?: boolean;
  bottomSlot?: React.ReactNode;
}

const OptionSearch = css`
  /* margin: 8px; */
  margin-bottom: ${spacings.s};
  position: sticky;
  top: 0;
  margin: calc(-1 * ${spacings.s});
  margin-bottom: 0;
  padding: ${spacings.s};
  background-image: linear-gradient(to bottom, ${color.hydrogen} 85%, transparent 85%);
  z-index: 1;

  > * {
    width: 100%;
  }
`;

const IconWrapper = styled.div`
  margin-right: ${spacings.xs};
  display: flex;
`;

const SelectOptionList = css`
  padding: ${spacings.s};
`;

const ClearButtonStyles = css`
  background: transparent;
  padding-right: 0;
`;

const Ellipsis = styled.span`
  font: ${fonts.body1};
  color: ${color.carbon900};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const NoOption = styled.div`
  color: ${color.carbon500};
  display: flex;
  align-items: flex-start;
  justify-content: center;
  height: 30px;
  width: 100%;
`;

const animationTimeout = easingBounceMs;

const ToggleArrow = styled(ArrowDownSLine)`
  flex: none;
  width: 16px;
  height: 16px;
  transition: transform ${easingBounceMs}ms ${easingBounce};
  fill: ${color.titanium500};
`;

const ArrowWrapper = styled.div`
  align-items: center;
  display: flex;
  margin-left: ${spacings.xs};
  flex-grow: 1;
`;

const StyledPopper = styled(Popper)`
  background-color: ${color.hydrogen};
  border: 1px solid ${color.titanium200};
  border-radius: ${radius};
  box-shadow: ${shadows.shadow100};
  color: ${color.carbon900};
  display: flex;
  flex-direction: column;
  max-height: 324px;
  overflow-y: auto;
  overscroll-behavior: contain;

  transition:
    transform ${animationTimeout}ms ${easingBounce},
    opacity ${animationTimeout}ms ${easingBounce};

  &,
  & > :first-child,
  & > :first-child > :first-child {
    &:focus-visible {
      outline: none;
    }
  }

  /* &:focus-within {
    border: 1px solid ${color.lightNitrogen500_Main};
  } */

  &.enter {
    opacity: 0;
    transform: scale(1, 0.3);
  }

  &.enter-active {
    opacity: 1;
    transform: none;
  }

  &.exit {
    opacity: 1;
    transform: none;
  }

  &.exit-active {
    opacity: 0;
    transform: scale(1, 0.3);
  }
`;

const VisuallyHidden = css`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const CustomSelectInput = styled.button`
  background-color: unset;
  position: relative;
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  border: unset;
  padding: 0 ${spacings.s};

  &:focus-visible {
    outline: none;
  }

  :not(.disabled) {
    cursor: pointer;
  }

  &.disabled {
    opacity: 0.5;
  }

  &.prompt {
    color: ${color.carbon900};
  }

  &:not(.disabled).prompt {
    opacity: 0.5;
  }

  ${ToggleArrow} {
    margin-left: auto;
  }

  &.opened {
    ${ToggleArrow} {
      transform: rotate(180deg);
    }
  }
`;

const findIndexOrThrow = (options: SelectOption[], value: string | number) => {
  const index = options.findIndex(o => o.value === value);

  if (index === -1) {
    console.debug(`Cannot find value ${value} in options`, { value, options });
    // throw new Error(`Cannot find value ${value} in options`);
  }

  return index;
};

const SelectContainer = styled.div`
  flex-grow: 1;
  max-width: 100%;
`;

const Select = (
  {
    prompt,
    options,
    disabled,
    value: propValue,
    onChange,
    className,
    style,
    required,
    error,
    multiple,
    filtered,
    buttonRef,
    tooltip,
    forcePromptWhenOptionSelected,
    clearable = true,
    autoScrollToSelected = false,
    // Field Props
    label,
    hint,
    revert,
    revertable,
    tooltipOverlay,
    noOptionsLabelElement,
    bottomSlot,
    ...selectProps
  }: P & Omit<FieldProps, "id">,
  forwardedRef: React.Ref<HTMLSelectElement>,
) => {
  if (typeof usePrevious(propValue) !== "undefined" && typeof propValue === "undefined")
    throw new Error(
      "Do not change between controlled and uncontrolled mode in select (value prop). Pass an empty string if needed. If you don't, the old value in state will be used, triggering bugs.",
    );

  const selectRef = useEnsuredForwardedRef(forwardedRef);
  const [opened, setOpened] = React.useState(false);
  const [valueState, setValueState] = React.useState<string | number | string[]>();

  const value = propValue === undefined ? valueState : propValue;

  const handleToggle = React.useCallback(() => {
    selectRef.current?.focus({ preventScroll: true });
    if (!disabled) setOpened(opened => !opened);
  }, [disabled, selectRef]);
  const extractFilterKey = React.useCallback((o: SelectOption) => o.label, []);

  const { filter, setFilter, filteredOptions } = useFilteredOptions(
    options,
    filtered ? extractFilterKey : undefined,
  );
  const handleFilterChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.currentTarget.value),
    [setFilter],
  );

  const selectedIndexes = React.useMemo(
    () =>
      (Array.isArray(value) ? value : [value])
        .filter((v): v is NonNullable<string> => v !== undefined && v !== "")
        .map(v => findIndexOrThrow(options, v))
        .filter(i => i > -1),
    [options, value],
  );

  const handleOptionClick = React.useCallback(
    (option: SelectOption) => {
      if (option.disabled || !selectRef.current) return;
      for (const o of Array.from(selectRef.current.options)) {
        if (o.value === option.value) {
          o.selected = multiple ? !o.selected : true;
        } else {
          o.selected = multiple ? o.selected : false;
        }
      }
      selectRef.current.dispatchEvent(new Event("change", { bubbles: true }));
      if (!multiple) setOpened(false);
    },
    [multiple, selectRef],
  );

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange?.(e);
      setValueState(
        multiple
          ? Array.from(e.currentTarget.selectedOptions).map(o => o.value)
          : e.currentTarget.value,
      );
    },
    [multiple, onChange],
  );

  const handleSelectKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLSelectElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.key === "Enter") {
        // replicate default HTMLSelect submit form behavior
        const submitButton = selectRef.current
          ?.closest("form")
          ?.querySelector("button[type=submit]") as HTMLButtonElement | null;
        submitButton?.click();
      }

      if (e.key === "Escape") setOpened(false);
      if (["ArrowDown", "ArrowUp"].includes(e.key)) setOpened(true);
      if ([" "].includes(e.key)) setOpened(opened => !opened);
    },
    [selectRef],
  );

  React.useEffect(() => {
    if (opened) setFilter("");
  }, [opened, setFilter]);

  const selected = React.useCallback(
    (option: SelectOption) => [value].flatMap(x => x).includes(option.value),
    [value],
  );

  const selectedOptionScrollIntoView = useCallback((el: HTMLDivElement) => {
    const selectedElement = el.querySelector(".selected") as HTMLElement | undefined;
    // Sometimes, when using others components, the parent is not focused, resulting in the scrollIntoView not working
    (el.parentNode as HTMLElement | null)?.focus();
    if (selectedElement) {
      window.setTimeout(() => {
        selectedElement.scrollIntoView({
          behavior: "auto",
          block: "start",
        });
      }, 0);
    }
  }, []);

  const overlay = React.useMemo(
    () => (
      <>
        <OptionList
          ref={autoScrollToSelected ? selectedOptionScrollIntoView : null}
          onRequestClose={handleToggle}
          className={SelectOptionList}
        >
          {filtered && (
            <div className={OptionSearch}>
              <SearchInputV2
                autoFocus
                placeholder={t("placeholder.search")}
                value={filter}
                onChange={handleFilterChange}
              />
            </div>
          )}
          {filteredOptions.map(option => (
            <OptionV2
              tabIndex={-1}
              key={option.value}
              selected={selected(option)}
              onClick={() => {
                if (multiple) return;
                handleOptionClick(option);
              }}
              disabled={option.disabled}
              withTextEllipsis
              title={option.label}
              noLabelElement={noOptionsLabelElement}
            >
              <FragmentSlot slot="text">{option.label}</FragmentSlot>
              {multiple && (
                <FragmentSlot slot="checkbox">
                  <Checkbox
                    slot="checkbox"
                    smallIndicator
                    checked={selected(option)}
                    onClick={() => {
                      handleOptionClick(option);
                    }}
                  />
                </FragmentSlot>
              )}
              <FragmentSlot slot="icon">
                {option.icon && <IconOption>{option.icon}</IconOption>}
              </FragmentSlot>
              {option.description !== undefined && (
                <FragmentSlot slot="description">{option.description}</FragmentSlot>
              )}
              <FragmentSlot slot="extra">{option.extra}</FragmentSlot>
            </OptionV2>
          ))}
          {!filteredOptions.length && <NoOption>{t("select.no_result")}</NoOption>}
        </OptionList>
        {bottomSlot}
      </>
    ),
    [
      autoScrollToSelected,
      bottomSlot,
      filter,
      filtered,
      filteredOptions,
      handleFilterChange,
      handleOptionClick,
      handleToggle,
      multiple,
      noOptionsLabelElement,
      selected,
      selectedOptionScrollIntoView,
    ],
  );

  const canClear = React.useMemo(() => {
    const hasValue = (multiple && Array.isArray(value) && value.length > 0) || (!multiple && value);
    return hasValue && !disabled && clearable;
  }, [multiple, value, disabled, clearable]);

  const SelectInputContent = React.useMemo(() => {
    // Display prompt if no option is selected
    if (forcePromptWhenOptionSelected || selectedIndexes.length === 0) {
      return <Ellipsis>{prompt}</Ellipsis>;
    }

    // Display the selected option with the extra component if only one option is selected
    if (selectedIndexes.length === 1 && selectedIndexes[0] !== undefined) {
      const option = options[selectedIndexes[0]];
      if (!option) throw new Error("Missing option");
      return (
        <>
          {option.icon && (
            <IconWrapper>
              <FragmentSlot slot="icon">
                <IconOption>{option.icon}</IconOption>
              </FragmentSlot>
            </IconWrapper>
          )}
          <FragmentSlot slot="text">
            <Ellipsis>{option.label}</Ellipsis>
          </FragmentSlot>
          <FragmentSlot slot="extra">
            <div>{option.extra}</div>
          </FragmentSlot>
        </>
      );
    }

    // Display all the selected options, labels only, comma separated
    return (
      <Ellipsis>
        {selectedIndexes
          .map(i => {
            const option = options[i];
            if (!option) throw new Error("Missing option");
            return option.label;
          })
          .join(", ")}
      </Ellipsis>
    );
  }, [forcePromptWhenOptionSelected, prompt, selectedIndexes, options]);

  return (
    <Field
      id=""
      label={label}
      error={error}
      focused={opened}
      disabled={disabled}
      required={required}
      className={className}
      revert={revert}
      revertable={revertable}
      tooltipOverlay={tooltipOverlay}
      hint={hint}
    >
      <TooltipV2 placement="top" overlay={tooltip ? tooltip : null}>
        <StyledPopper
          placement="bottom"
          matchChildWidth
          opened={opened}
          setOpened={setOpened}
          gap={4}
          animationTimeout={animationTimeout}
          closeOnOutsideClick
          overlay={overlay}
        >
          <SelectContainer>
            <select
              className={VisuallyHidden}
              aria-hidden="true"
              ref={selectRef}
              value={value}
              onKeyDown={handleSelectKeyDown}
              onChange={handleChange}
              disabled={disabled}
              required={required}
              multiple={multiple}
              {...selectProps}
            >
              {options.map(option => (
                <option key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </option>
              ))}
            </select>

            <CustomSelectInput
              type="button"
              ref={buttonRef}
              onClick={handleToggle}
              style={style}
              className={classNames({
                opened,
                required,
                disabled,
                prompt: !selectedIndexes.length,
              })}
            >
              {SelectInputContent}
              <ArrowWrapper className={HIDE_ON_EXPORT}>
                {canClear && (
                  <ClearButton
                    className={ClearButtonStyles}
                    // custom clear logic to prevent menu from opening on clear
                    onClick={e => {
                      e.stopPropagation();
                      if (!selectRef.current) return;
                      selectRef.current.value = "";
                      selectRef.current.dispatchEvent(new Event("change", { bubbles: true }));
                    }}
                  />
                )}
                <ToggleArrow fill={colors.titanium500} />
              </ArrowWrapper>
            </CustomSelectInput>
          </SelectContainer>
        </StyledPopper>
      </TooltipV2>
    </Field>
  );
};

export default React.memo(React.forwardRef(Select));
